
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Satyaki Bose
 * CustomToast
 * Reusable Custom Toast component
 */


import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  IconButton,
  Slide,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Icon from '../icons/Icon';
import mixins from '../../assets/styles/mixins.scss';
import CommonStyles from '../../assets/styles/CommonStyles';

const SnackbarContent = styled('div')({
  '&&': {
    minWidth: 396,
    maxWidth: 396,
    borderRadius: 8,
    backgroundColor: '#F6F6FB',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'stretch',
    border: '1px solid #e0e0e0',
    height: 118,
    padding: 2,
    boxShadow: '0px 4px 20px rgba(13, 0, 77, 0.05), 0px 4px 20px rgba(13, 0, 77, 0.10), 0px 4px 20px rgba(13, 0, 77, 0.15)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      width: 4,
      height: '100%',
      borderRadius: '2px 0 0 2px',
      backgroundColor: 'var(--sidebar-color)',
      zIndex: 1
    }
  }
});

const ContentContainer = styled('div')({
  '&&': {
    flex: 1,
    padding: '8px 16px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    position: 'relative',
    zIndex: 2,
    justifyContent: 'center'
  }
});

const CloseButton = styled(IconButton)({
  '&&': {
    position: 'absolute',
    top: 6,
    right: 12,
    padding: 4,
    color: mixins.navyColor
  }
});

const IconContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center'
  }
});

const StatusIconWrapper = styled('span')({
  '&&': {
    fontSize: 20,
    marginRight: 12,
    display: 'inline-flex',
    alignItems: 'center'
  }
});

const TitleText = styled(Typography)({
  '&&': {
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight600,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.navyColor,
    margin: 0,
    lineHeight: mixins.lineHeight24
  }
});

const MessageText = styled(Typography)({
  '&&': {
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: mixins.lineHeight24,
    color: mixins.navyColor,
    margin: 0,
    ...CommonStyles().truncateNumberOfLines(2),
    '& b, & strong': {
      fontWeight: mixins.fontWeight600
    }
  }
});

const ActionText = styled(Typography)({
  '&&': {
    fontSize: mixins.fontSize16,
    lineHeight: mixins.lineHeight24,
    margin: 0,
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  }
});

const CustomToast = ({
  open = false,
  onClose,
  title = '',
  message = '',
  actionText = '',
  actionTextStyle,
  onActionClick,
  variant,
  customColors,
  autoHideDuration,
  position,
  showCloseButton,
  ...props
}) => {
  const getIcon = () => {
    const iconProps = {
      style: { color: customColors.accentColor1 }
    };

    switch (variant) {
      case 'info':
        return <StatusIconWrapper><Icon name="FeedbackInfoIcon" {...iconProps} /></StatusIconWrapper>;
      case 'error':
        return <StatusIconWrapper><Icon name="FeedbackFailureIcon" {...iconProps} /></StatusIconWrapper>;
      case 'custom':
        return <StatusIconWrapper><Icon name="ToastIconTick" {...iconProps} /></StatusIconWrapper>;
      default:
        return <StatusIconWrapper><Icon name="ToastIconTick" {...iconProps} /></StatusIconWrapper>;
    }
  };
  const getAnchorOrigin = () => {
    const positions = {
      'top-right': { vertical: 'top', horizontal: 'right' },
      'top-left': { vertical: 'top', horizontal: 'left' },
      'bottom-right': { vertical: 'bottom', horizontal: 'right' },
      'bottom-left': { vertical: 'bottom', horizontal: 'left' }
    };

    return positions[position] || positions['top-right'];
  };

  // String messages may include trusted HTML (e.g. <b>title</b>); nodes render as children.
  const renderMessage = () => {
    if (!message) {
      return null;
    }

    if (typeof message === 'string') {
      return (
        <MessageText
          component="div"
          dangerouslySetInnerHTML={{ __html: message }}
        />
      );
    }

    return <MessageText component="div">{message}</MessageText>;
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={getAnchorOrigin()}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'left' }}
      transitionDuration={{ enter: 500, exit: 450 }}
      {...props}
    >
      <SnackbarContent
        style={{ '--sidebar-color': customColors.accentColor2 }}
      >
        <ContentContainer>
          {showCloseButton && (
            <CloseButton
              onClick={onClose}
              size="small"
            >
              <Icon name="CloseIcon" />
            </CloseButton>
          )}

          <IconContainer>
            {getIcon()}
            {title && (
              <TitleText>
                {title}
              </TitleText>
            )}
          </IconContainer>

          {renderMessage()}

          {actionText && (
            <ActionText
              onClick={onActionClick}
              style={{ color: customColors.accentColor1, ...actionTextStyle }}
            >
              {actionText}
            </ActionText>
          )}
        </ContentContainer>
      </SnackbarContent>
    </Snackbar>
  );
};

CustomToast.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  actionText: PropTypes.string,
  actionTextStyle: PropTypes.object,
  onActionClick: PropTypes.func,
  variant: PropTypes.string,
  customColors: PropTypes.object,
  autoHideDuration: PropTypes.number,
  position: PropTypes.string,
  showCloseButton: PropTypes.bool
};

CustomToast.defaultProps = {
  open: false,
  onClose: () => {},
  title: '',
  message: '',
  actionText: '',
  actionTextStyle: {},
  onActionClick: () => {},
  variant: 'success',
  customColors: {
  },
  autoHideDuration: 5000,
  position: 'top-right',
  showCloseButton: true
};

export default CustomToast;
